import { PlanOverrideProps } from 'store/signup/signup.types';

import { DEFAULT_SCALE_ADDON_CODE } from 'constants/defaults';

import { MembershipPlan, PlanCodes, PricePoint } from 'models/plans.types';
import { UserProgram } from 'models/programs.types';

type PaymentInterval = { qty: number; units: 'days' | 'months' | 'years' };

/**
 * Builds the compact `period` code used in the `begin_checkout` / `purchase` analytics events
 * from a subscription's billing interval — a 3-month plan -> "3m", a 30-day trial -> "30d",
 * a yearly plan -> "1y". The format mirrors the mobile app so web and mobile events match.
 * Returns `undefined` for one-time purchases (no interval), so the caller simply omits `period`.
 */
export const shortPeriodCode = (interval?: PaymentInterval | null): string | undefined => {
  if (!interval) return undefined;
  const unitLetter = { days: 'd', months: 'm', years: 'y' }[interval.units];
  if (!unitLetter) return undefined;
  return `${interval.qty}${unitLetter}`;
};

export const overridePlans = (
  membershipPlans: MembershipPlan[],
  planOverrides: PlanOverrideProps[]
): MembershipPlan[] => {
  return [...membershipPlans].map((membershipPlan) => {
    const overrideData = planOverrides.find((pp) => pp.planCode === membershipPlan.planCode);
    if (!overrideData) {
      return membershipPlan;
    }
    const updatedPlan = { ...membershipPlan };
    updatedPlan.pricePoints = membershipPlan.pricePoints.map((pp) => {
      if (pp.planPricePointId !== overrideData.planPricePointId) {
        return { ...pp, isDefault: false };
      }
      const overrideTotalCost = overrideData.totalCost;
      const overridePaymentInterval = overrideData.paymentInterval;
      const overrideAppointmentCost = overrideData.appointmentCostInDollars;
      const planPriceId = overrideData.planPricePointId;
      const appointmentPriceId = overrideData.appointmentPricePointId;
      const monthlyPlanCost = overrideData.planMonthlyCostInDollars;
      const componentId = overrideData.componentId;
      return {
        ...pp,
        isDefault: true,
        ...(monthlyPlanCost && { monthlyPrice: monthlyPlanCost }),
        ...(overrideTotalCost && { totalCost: overrideTotalCost }),
        ...(overridePaymentInterval && { paymentInterval: overridePaymentInterval }),
        ...(overrideAppointmentCost && { initialAppointmentCost: overrideAppointmentCost }),
        ...(planPriceId && { planPriceId }),
        ...(appointmentPriceId && { initialAppointmentPricePointId: appointmentPriceId }),
        ...(componentId && { initialAppointmentComponentId: componentId })
      };
    });
    if (updatedPlan.pricePoints.length > 0 && !updatedPlan.pricePoints.some((pp) => pp.isDefault)) {
      updatedPlan.pricePoints[0].isDefault = true;
    }
    return updatedPlan;
  });
};

export const getPricePoint = (
  pricePoints: PricePoint[],
  specificPricePoints: string[],
  qty?: number
) =>
  pricePoints.find((pp) =>
    !!specificPricePoints.length
      ? specificPricePoints.includes(pp.planPricePointId)
      : typeof qty === 'number'
        ? pp.paymentInterval.qty === qty
        : pp.isDefault
  );

export const getOrderTotalPrice = (
  selectedPlan: MembershipPlan,
  selectedPricePoint: PricePoint,
  isInitial = true
) => {
  if ([PlanCodes.LifeMDMembership, PlanCodes.FlexCare].includes(selectedPlan.planCode)) {
    return isInitial
      ? selectedPricePoint.initialAppointmentCost
      : selectedPricePoint.subsequentAppointmentCost;
  }
  return selectedPricePoint.totalCost;
};

export const getScalesAddonData = (pp?: PricePoint, includesScales?: boolean) => {
  if (!pp || !includesScales) {
    return null;
  }
  const addon = pp.addOns?.find((a) => a.code === DEFAULT_SCALE_ADDON_CODE);
  if (!addon) {
    return null;
  }
  return { code: addon.code, unitPrice: addon.unitPrice };
};

export const findMaintenancePP = (pps: PricePoint[] | undefined, { keyword = '', qty = 1 }) => {
  if (!pps?.length) {
    return null;
  }
  return pps.find(
    (pp) =>
      pp.isWeightMaintenance &&
      pp.paymentInterval.qty === qty &&
      pp.planPricePointId.includes(keyword)
  );
};

export const calculateDiscountPercentageByPrices = (price: number, oldPrice: number) => {
  return Math.ceil(((oldPrice - price) / oldPrice) * 100);
};

/**
 * The price point a program is enrolled on, out of a list spanning every active program.
 *
 * Matches on `programId` AND `planPricePointId` because neither identifies a row on its own: the
 * list spans every active program, and one plan contributes several price points under the same
 * `programId` (its term variants), so a `programId`-only match returns whichever term comes first.
 * A `planPricePointId`-only match has the mirror problem across programs. Either way the symptom
 * is a silently wrong insurance classification rather than an error.
 */
export const findProgramPricePoint = (
  pricePoints: PricePoint[],
  program?: Pick<UserProgram['program'], '_id' | 'planPricePointId'>
): PricePoint | undefined => {
  if (!program) return undefined;

  return pricePoints.find(
    (pp) => pp.programId === program._id && pp.planPricePointId === program.planPricePointId
  );
};
